/**
 * The Featured rail (DENG-1294): posts a community moderator pinned to the top of a community.
 *
 * A featured post carries `featured: true`, `featured_at` and `featured_by` on its Firestore
 * document. It shows only in the rail — the feed skips it — and only while that one community
 * is selected. Deliberately not the older `pinned` field: that is beta's announcement flag,
 * and legacy community posts may still carry it.
 *
 * Shared by web and ns-myfender so both pick the same card media and the same order.
 */

/** The most posts a community's rail shows; moderators are told when they hit it. */
export const FEATURED_RAIL_MAX = 10;

/** Fields the feature writes. Firestore rules let only the community's moderators touch them. */
export const FEATURED_FIELDS = ["featured", "featured_at", "featured_by"] as const;

export type FeaturedMediaKind = "image" | "youtube" | "video" | "audio" | "none";

/** What a rail card shows above the text. `imageUrl` is empty for audio and plain-text posts. */
export interface FeaturedMedia {
  kind: FeaturedMediaKind;
  imageUrl: string;
}

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "heic"];
const AUDIO_EXTENSIONS = ["wav", "mp3", "aiff", "aif", "m4a", "flac", "aac", "ogg"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "m4v", "webm"];

/**
 * Whether a view shows the rail — and so whether its feed leaves featured posts out, since the
 * two go together: a post is never in both, and never in neither. The rail belongs to ONE
 * community's own feed: not the all-communities hub, not a beta program, not a search (results
 * should find a featured post like any other), and not My Posts / Saved Posts, which are lists
 * of the reader's own threads rather than the community's.
 */
export function featuredRailApplies(view: {
  enabled: boolean;
  communityStringId?: string | null;
  betaStringId?: string | null;
  searchQuery?: string | null;
  channelStringId?: string | null;
}): boolean {
  if (!view?.enabled || !view.communityStringId || view.betaStringId) return false;
  if ((view.searchQuery || "").trim()) return false;
  return view.channelStringId !== "my_posts" && view.channelStringId !== "pinned_posts";
}

/** Live in the rail: flagged, and not deleted since. */
export function isFeaturedPost(post: any): boolean {
  return !!post?.featured && !post.deleted;
}

/**
 * Featured changes made on this device, by post id, with when they were made.
 *
 * The feeds read from Algolia, whose copy of a post trails the Firestore write by a few
 * seconds. So a post just taken out of the rail came back still `featured: true` in the feed's
 * reload — and was filtered out of it again, in neither place — and a post just featured could
 * slip back into the feed. This device's own change wins until the index has caught up.
 */
const _recentFeaturedChanges = new Map<string, { featured: boolean; at: number }>();

/** How long this device's change outranks the index — far longer than Algolia takes to sync. */
export const FEATURED_CHANGE_TTL_MS = 2 * 60 * 1000;

/** Record a feature / un-feature made here (the rail, the composer, a post's menu). */
export function rememberFeaturedChange(postId: string, featured: boolean, now: number = Date.now()) {
  if (postId) _recentFeaturedChanges.set(`${postId}`, { featured, at: now });
}

/**
 * `isFeaturedPost`, but this device's own recent change wins over the post's stored flag —
 * what the feeds should test, since their copy of the post may be stale. See above.
 */
export function isFeaturedNow(post: any, now: number = Date.now()): boolean {
  const id = post?.id || post?.objectID || post?.key;
  const change = id ? _recentFeaturedChanges.get(`${id}`) : undefined;
  if (change && now - change.at < FEATURED_CHANGE_TTL_MS) return change.featured && !post.deleted;
  if (change) _recentFeaturedChanges.delete(`${id}`);
  return isFeaturedPost(post);
}

/** Tests only. */
export function clearFeaturedChanges() {
  _recentFeaturedChanges.clear();
}

/** A Firestore Timestamp, Date, epoch number or ISO string, as epoch ms; 0 when absent. */
export function featuredAtMillis(value: any): number {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (typeof value._seconds === "number") return value._seconds * 1000;
  const num = typeof value === "number" ? value : Date.parse(value);
  if (!num || isNaN(num)) return 0;
  return num < 1e12 ? num * 1000 : num;
}

/** The rail's order: most recently featured first, capped at FEATURED_RAIL_MAX. Leaves the input alone. */
export function sortFeaturedPosts<T = any>(posts: T[]): T[] {
  return (posts || [])
    .filter(isFeaturedPost)
    .sort((a: any, b: any) => featuredAtMillis(b.featured_at) - featuredAtMillis(a.featured_at))
    .slice(0, FEATURED_RAIL_MAX);
}

/** YouTube's own still for a video id; `hqdefault` exists for every video, unlike `maxresdefault`. */
export function youtubeThumbnailUrl(videoId: string): string {
  return videoId ? `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg` : "";
}

/**
 * The card's media, from the post's first attachment: an uploaded image shows itself, a Learn
 * item its cover (a video's with a play button), a pasted YouTube link its thumbnail, an
 * uploaded audio file a play button, an uploaded video a play tile. An upload wins over a
 * Learn item, and both over a link, as uploads do in the feed's list rows.
 */
export function featuredMediaFor(post: any): FeaturedMedia {
  const file = post?.files?.[0];
  if (file) {
    const ext = String(file.extension || "").toLowerCase();
    if (IMAGE_EXTENSIONS.includes(ext)) return { kind: "image", imageUrl: file.url || "" };
    if (AUDIO_EXTENSIONS.includes(ext)) return { kind: "audio", imageUrl: "" };
    if (VIDEO_EXTENSIONS.includes(ext)) return { kind: "video", imageUrl: file.thumbnail || file.poster || "" };
  }
  // An item from Learn: `learn_content[].content` carries its cover image. A video plays, so
  // it gets the play button; a course or lesson is just its cover.
  const learn = (post?.learn_content || []).find((item: any) => item?.content?.image)?.content;
  if (learn) return { kind: learn.type === "video" ? "video" : "image", imageUrl: learn.image };
  const youtube = (post?.embeds || []).find((e: any) => e?.type === "youtube" && e.id);
  if (youtube) return { kind: "youtube", imageUrl: youtubeThumbnailUrl(youtube.id) };
  return { kind: "none", imageUrl: "" };
}

/** The fields to write when a moderator features or un-features a post. `now` is a server timestamp sentinel or a Date. */
export function featuredUpdate(featured: boolean, userId: string | number, now: any): Record<string, any> {
  return featured ? { featured: true, featured_at: now, featured_by: `${userId}` } : { featured: false, featured_at: null, featured_by: null };
}
